import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { Spinner, SkeletonRows } from "../../Components/Common/LoadingStates";
import { APIClient } from "../../helpers/api_helper";
import { GET_TRANSACTION_HISTORY, UPDATE_TRANSACTION_STATUS } from "../../helpers/url_helper";
import { getPaybisTransactionList } from "../../helpers/fakebackend_helper";
import { useCachedRequest, invalidateCache } from "../../helpers/cachedFetch";

const TX_TYPES = [
  "All",
  "Transfer",
  "Swap",
  "On-Ramp",
  "Off-Ramp",
];

const api = new APIClient();

const StatusBadge = ({ status }) => {
  const normalized = (status || "").toString().toUpperCase();

  const config = {
    PENDING: { bg: "bg-amber-500/16", text: "text-amber-300", label: "Pending" },
    CONFIRMED: { bg: "bg-green-500/16", text: "text-green-300", label: "Confirmed" },
    FAILED: { bg: "bg-red-500/16", text: "text-red-300", label: "Failed" },
    CANCELLED: { bg: "bg-gray-700", text: "text-gray-300", label: "Cancelled" },
    CANCELED: { bg: "bg-gray-700", text: "text-gray-300", label: "Canceled" },
    PROCESSING: { bg: "bg-cyan-500/16", text: "text-cyan-300", label: "Processing" },
    COMPLETE: { bg: "bg-green-500/16", text: "text-green-300", label: "Complete" },
    COMPLETED: { bg: "bg-green-500/16", text: "text-green-300", label: "Completed" },
  };

  const { bg, text, label } = config[normalized] || {
    bg: "bg-gray-700",
    text: "text-gray-300",
    label: status || "-",
  };
  return (
    <span className={`px-2 py-1 text-xs font-normal rounded ${bg} ${text}`}>
      {label}
    </span>
  );
};

const EDITABLE_STATUSES = ["PENDING", "CONFIRMED", "FAILED", "CANCELLED"];

// Status cell with an inline edit-pencil for super admins. The pencil opens a
// small dropdown to manually override a transaction's status — the human
// fallback for when the on-chain reconciler can't resolve a row (no verifier
// for the chain, etc.). Backend gates the write to super admin; the pencil is
// hidden for everyone else and never shown on Paybis rows (different source).
const StatusCell = ({ id, status, canEdit, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState((status || "PENDING").toString().toUpperCase());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  if (!editing) {
    return (
      <div className="flex items-center gap-1.5">
        <StatusBadge status={status} />
        {canEdit && id != null && (
          <button
            type="button"
            title="Edit status (super admin)"
            onClick={(e) => {
              e.stopPropagation();
              setValue((status || "PENDING").toString().toUpperCase());
              setErr("");
              setEditing(true);
            }}
            className="text-gray-500 hover:text-bl-accent-light transition-colors"
          >
            <i className="ri-pencil-line text-sm"></i>
          </button>
        )}
      </div>
    );
  }

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      await api.update(`${UPDATE_TRANSACTION_STATUS}/${id}/status`, { status: value });
      invalidateCache("transactions:");
      setEditing(false);
      if (onSaved) onSaved();
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || "Failed to update");
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <select
        value={value}
        onChange={(e) => setValue(e.target.value)}
        disabled={saving}
        className="px-2 py-1 text-xs border border-gray-700 rounded bg-gray-800 text-gray-100 focus:outline-none focus:ring-1 focus:ring-bl-accent"
      >
        {EDITABLE_STATUSES.map((s) => (
          <option key={s} value={s}>{s}</option>
        ))}
      </select>
      <button
        type="button"
        title="Save"
        onClick={save}
        disabled={saving}
        className="text-green-400 hover:text-green-300 disabled:opacity-50"
      >
        <i className="ri-check-line text-base"></i>
      </button>
      <button
        type="button"
        title="Cancel"
        onClick={() => { setEditing(false); setErr(""); }}
        disabled={saving}
        className="text-gray-400 hover:text-gray-200 disabled:opacity-50"
      >
        <i className="ri-close-line text-base"></i>
      </button>
      {err && <span className="text-[10px] text-red-400 ml-1" title={err}>!</span>}
    </div>
  );
};

// Block explorer base URL per chain (keyed by the uppercase chain code the
// data uses — same labels ChainPill renders). Add new chains here as the app
// supports more swap routes. Unknown chains fall through to a plain, non-linked
// hash so we never point at the wrong explorer.
const EXPLORER_TX_BASE = {
  ETH: "https://etherscan.io/tx/",
  ETHEREUM: "https://etherscan.io/tx/",
  BSC: "https://bscscan.com/tx/",
  BNB: "https://bscscan.com/tx/",
  POLYGON: "https://polygonscan.com/tx/",
  MATIC: "https://polygonscan.com/tx/",
  AVAX: "https://snowtrace.io/tx/",
  AVALANCHE: "https://snowtrace.io/tx/",
  ARBITRUM: "https://arbiscan.io/tx/",
  ARB: "https://arbiscan.io/tx/",
  OPTIMISM: "https://optimistic.etherscan.io/tx/",
  OP: "https://optimistic.etherscan.io/tx/",
  BASE: "https://basescan.org/tx/",
  BTC: "https://blockchair.com/bitcoin/transaction/",
  BITCOIN: "https://blockchair.com/bitcoin/transaction/",
  LTC: "https://blockchair.com/litecoin/transaction/",
  LITECOIN: "https://blockchair.com/litecoin/transaction/",
  DOGE: "https://blockchair.com/dogecoin/transaction/",
  BCH: "https://blockchair.com/bitcoin-cash/transaction/",
  SOL: "https://solscan.io/tx/",
  SOLANA: "https://solscan.io/tx/",
  TRON: "https://tronscan.org/#/transaction/",
  TRX: "https://tronscan.org/#/transaction/",
};

const explorerTxUrl = (network, hash) => {
  if (!hash) return null;
  const base = EXPLORER_TX_BASE[String(network || "").trim().toUpperCase()];
  return base ? base + hash : null;
};

// A transaction hash cell. When we can resolve an explorer for its chain (or an
// explicit url is supplied, e.g. Paybis' own explorer_link), the truncated hash
// becomes a link that opens the explorer in a new tab; otherwise it's plain text.
const TxHash = ({ hash, network, url }) => {
  if (!hash) return <span className="text-gray-400">-</span>;
  const href = url || explorerTxUrl(network, hash);
  const short = `${hash.slice(0, 8)}...`;
  if (!href) {
    return (
      <span className="text-gray-300 font-mono" title={hash}>{short}</span>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={`Open in explorer: ${hash}`}
      onClick={(e) => e.stopPropagation()}
      className="text-bl-accent-light font-mono hover:underline inline-flex items-center gap-1"
    >
      {short}
      <i className="ri-external-link-line text-[11px] opacity-70"></i>
    </a>
  );
};

const ChainPill = ({ label }) => {
  const getClasses = (label) => {
    switch (label) {
      case "ETH":
        return "bg-bl-accent/16 text-bl-accent-light";
      case "POLYGON":
        return "bg-purple-500/16 text-purple-300";
      case "BSC":
        return "bg-amber-500/16 text-amber-300";
      case "BTC":
        return "bg-cyan-500/16 text-cyan-300";
      default:
        return "bg-gray-700 text-gray-300";
    }
  };
  return (
    <span className={`px-2 py-1 text-xs font-normal rounded ${getClasses(label)} mr-1`}>
      {label}
    </span>
  );
};

const TypePill = ({ type }) => {
  const getClasses = (t) => {
    switch (t) {
      case "SEND":
        return "bg-red-500/16 text-red-300";
      case "TRANSFER":
        return "bg-green-500/16 text-green-300";
      case "SWAP":
        return "bg-bl-accent/16 text-bl-accent-light";
      case "ON-RAMP":
      case "On-Ramp (Paybis)":
        return "bg-cyan-500/16 text-cyan-300";
      case "OFF-RAMP":
      case "Off-Ramp (Paybis)":
        return "bg-gray-700 text-gray-300";
      default:
        return "bg-gray-700 text-gray-300";
    }
  };
  return (
    <span className={`px-2 py-1 text-xs font-normal rounded ${getClasses(type)}`}>
      {type}
    </span>
  );
};

// SwapKit's explorer (track.swapkit.dev) wants a numeric EVM chainId. Map the
// chain codes our data uses to their chainId. Non-EVM chains (THOR/MAYA/BTC/…)
// aren't keyed by a numeric id there, so we fall back to chainId=1 — the hash
// still resolves the swap in their explorer regardless of the chainId hint.
const SWAPKIT_CHAIN_ID = {
  ETH: 1,
  ETHEREUM: 1,
  BSC: 56,
  BNB: 56,
  POLYGON: 137,
  MATIC: 137,
  AVAX: 43114,
  AVALANCHE: 43114,
  ARBITRUM: 42161,
  ARB: 42161,
  OPTIMISM: 10,
  OP: 10,
  BASE: 8453,
};

const swapkitTrackUrl = (hash, network) => {
  if (!hash) return null;
  const chainId = SWAPKIT_CHAIN_ID[String(network || "").trim().toUpperCase()] || 1;
  return `https://track.swapkit.dev/?hash=${hash}&chainId=${chainId}`;
};

// The route/provider a transaction went through (SwapKit, Rango, Paybis, …).
// Reads the swap_partner field; "-" when there's no route (e.g. a plain send).
// For SwapKit swaps, the pill links to that specific swap on SwapKit's explorer
// (track.swapkit.dev) so the full from/to + route details are one click away.
const SourcePill = ({ label, hash, network }) => {
  if (!label) return <span className="text-sm text-gray-400">-</span>;
  const norm = String(label).toLowerCase();
  const cls = norm.includes("swapkit")
    ? "bg-bl-accent/16 text-bl-accent-light"
    : norm.includes("rango")
    ? "bg-purple-500/16 text-purple-300"
    : norm.includes("cowswap")
    ? "bg-amber-500/16 text-amber-300"
    : norm.includes("paybis")
    ? "bg-cyan-500/16 text-cyan-300"
    : "bg-gray-700 text-gray-300";
  const pretty = label.charAt(0).toUpperCase() + label.slice(1);
  const trackUrl = norm.includes("swapkit") ? swapkitTrackUrl(hash, network) : null;
  if (trackUrl) {
    return (
      <a
        href={trackUrl}
        target="_blank"
        rel="noopener noreferrer"
        title="View this swap on SwapKit explorer"
        onClick={(e) => e.stopPropagation()}
        className={`px-2 py-1 text-xs font-normal rounded ${cls} hover:underline inline-flex items-center gap-1`}
      >
        {pretty}
        <i className="ri-external-link-line text-[11px] opacity-70"></i>
      </a>
    );
  }
  return <span className={`px-2 py-1 text-xs font-normal rounded ${cls}`}>{pretty}</span>;
};

// "From → To" shown as two stacked, width-capped lines. A long value (full
// address) is clipped to the cell and scrolls horizontally INSIDE it, so the
// column never blows out the table width. Hover shows the full value.
const FROMTO_MAX_W = 220;
const FromToLine = ({ label, value }) => (
  <div
    title={value || "-"}
    className="flex items-center gap-1 overflow-x-auto whitespace-nowrap"
    style={{ maxWidth: FROMTO_MAX_W }}
  >
    <span className="text-gray-500 shrink-0">{label}</span>
    <span className="font-mono text-gray-200">{value || "-"}</span>
  </div>
);
const FromToCell = ({ from, to }) => (
  <div className="space-y-0.5" style={{ maxWidth: FROMTO_MAX_W }}>
    <FromToLine label="From" value={from} />
    <FromToLine label="To" value={to} />
  </div>
);

const Transactions = ({ embedded = false } = {}) => {
  document.title = "Transactions | BiorGov - BiorGov Admin & Dashboard";

  const isSuperAdmin = useSelector((s) => s.Profile.is_super_admin);

  const [activeType, setActiveType] = useState("All");
  const [dateRange, setDateRange] = useState("All Time");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const showPaybis = activeType === "All" || activeType === "On-Ramp" || activeType === "Off-Ramp";

  // Backend now normalizes filters; frontend sends raw UI values

  const timeAgo = (ts) => {
    if (!ts) return "-";
    const t = typeof ts === "number" ? new Date(ts) : new Date(ts);
    const diffMs = Date.now() - t.getTime();
    const mins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMs / 3600000);
    const days = Math.floor(diffMs / 86400000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins} min ago`;
    if (hours < 24) return `${hours} hr ago`;
    if (days < 7) return `${days} day${days !== 1 ? "s" : ""} ago`;
    return t.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const txReq = useCachedRequest({
    key: `transactions:list:${activeType}:${dateRange}:${query.trim()}:${page}:${pageSize}`,
    fetcher: async () => {
      const params = {
        limit: pageSize,
        offset: (page - 1) * pageSize,
        sort_by: "timestamp",
        sort_order: "DESC",
        type: activeType,
        date_range: dateRange,
      };
      if (query && query.trim()) params.q = query.trim();
      // axios interceptor already unwraps `.data`, so `res` IS the JSON body.
      return api.get(GET_TRANSACTION_HISTORY, params);
    },
    deps: [activeType, dateRange, query, page, pageSize],
  });
  const rows = txReq.data?.transactions || [];
  const total = txReq.data?.pagination?.total ?? rows.length;
  const loading = txReq.loading;
  const error = txReq.error ? (typeof txReq.error === "string" ? txReq.error : txReq.error?.message || "Failed to load transactions") : "";

  const paybisReq = useCachedRequest({
    key: "transactions:paybis:50",
    fetcher: () => getPaybisTransactionList({
      limit: 50,
      offset: 0,
      sort_by: "created_at",
      sort_order: "DESC",
    }).catch(() => ({ transactions: [] })),
    enabled: showPaybis,
  });
  const paybisRows = (showPaybis ? paybisReq.data?.transactions : null) || [];
  const paybisLoading = showPaybis && paybisReq.loading;

  const filtered = useMemo(() => rows, [rows]);

  const parsePaybisJson = (val) => {
    if (val == null || val === "") return null;
    if (typeof val === "object") return val;
    try {
      return typeof val === "string" ? JSON.parse(val) : val;
    } catch {
      return null;
    }
  };

  const normalizedPaybisRows = useMemo(() => {
    if (!showPaybis || !paybisRows.length) return [];
    return paybisRows.map((r) => {
      const af = parsePaybisJson(r.amount_from);
      const at = parsePaybisJson(r.amount_to);
      const payout = parsePaybisJson(r.payout);
      const transaction = parsePaybisJson(r.transaction);
      const flow = transaction?.flow || "buyCrypto"; // buyCrypto = on-ramp, sellCrypto = off-ramp
      return {
        _paybis: true,
        _flow: flow,
        id: r.id,
        request_id: r.request_id,
        created_at: r.created_at,
        timestamp: r.created_at ? new Date(r.created_at).getTime() : 0,
        transaction_status: r.transaction_status,
        amount_from: af,
        amount_to: at,
        payout,
        partner_user_id: r.partner_user_id,
      };
    });
  }, [showPaybis, paybisRows]);

  const getSortTime = (r) => {
    if (r._paybis) return r.timestamp || 0;
    const t = r.timestamp;
    return t ? new Date(t).getTime() : 0;
  };

  const combinedList = useMemo(() => {
    // Paybis rows are a fixed top-50 list (always fetched at offset 0), NOT
    // paginated alongside the server transactions. Merge them only on page 1,
    // otherwise the same 50 rows reappear on every page (double-counting).
    if (!showPaybis || page !== 1 || normalizedPaybisRows.length === 0) return filtered;
    const paybisFiltered =
      activeType === "On-Ramp"
        ? normalizedPaybisRows.filter((r) => r._flow === "buyCrypto")
        : activeType === "Off-Ramp"
        ? normalizedPaybisRows.filter((r) => r._flow === "sellCrypto")
        : normalizedPaybisRows;
    const combined = [...filtered, ...paybisFiltered];
    combined.sort((a, b) => getSortTime(b) - getSortTime(a));
    return combined;
  }, [filtered, normalizedPaybisRows, showPaybis, activeType, page]);

  return (
    <div className={embedded ? "" : "min-h-screen"}>
      <div className={embedded ? "w-full min-w-0" : "w-full px-4 sm:px-6 lg:px-8 py-8"}>
        {!embedded && <BreadCrumb title="Transactions" pageTitle="Monitoring" />}

        {/* Filters toolbar */}
        <div className="mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex flex-wrap gap-2">
              {TX_TYPES.map((t) => (
                <button
                  key={t}
                  onClick={() => setActiveType(t)}
                  className={`px-4 py-2 text-sm font-normal rounded-md transition-colors ${
                    activeType === t
                      ? "bg-bl-accent text-white"
                      : "bg-gray-800 text-gray-300 border border-gray-700 hover:bg-bl-accent/10"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 lg:justify-end">
              <input
                type="text"
                placeholder="Search by Transaction ID, wallet, amount..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-700 rounded-md shadow-sm bg-gray-800 text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-bl-accent focus:border-bl-accent transition-colors flex-1 min-w-[200px]"
              />
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-700 rounded-md shadow-sm bg-gray-800 text-gray-100 focus:outline-none focus:ring-1 focus:ring-bl-accent focus:border-bl-accent transition-colors max-w-[140px]"
              >
                {["Today", "7 Days", "30 Days", "All Time"].map((opt) => (
                  <option key={opt}>{opt}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Transaction table */}
        <div className="bl-glass">
          <div className="p-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
              <div className="text-sm text-gray-400">
                Transaction history
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(parseInt(e.target.value));
                    setPage(1);
                  }}
                  className="px-3 py-2 text-sm border border-gray-700 rounded-md shadow-sm bg-gray-800 text-gray-100 focus:outline-none focus:ring-1 focus:ring-bl-accent focus:border-bl-accent transition-colors"
                >
                  {[10, 25, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n} / page
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
                {error}
              </div>
            )}
            
            {(loading || (showPaybis && paybisLoading)) && (
              <div className="text-sm text-gray-400 mb-4 flex items-center gap-2">
                <Spinner size={14} />
                <span>Loading transactions…</span>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-bl-accent/5">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Transaction ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Time
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Source
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Wallet
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      From/To
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Token
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Chains
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Fee
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Source Tx
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Dest Tx
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-700">
                  {(loading || (showPaybis && paybisLoading)) && combinedList.length === 0 ? (
                    <SkeletonRows rows={6} cols={13} />
                  ) : combinedList.length === 0 ? (
                    <tr>
                      <td colSpan="13" className="px-4 py-8 text-center text-sm text-gray-400">
                        No transactions found
                      </td>
                    </tr>
                  ) : (
                    combinedList.map((r) => {
                      if (r._paybis) {
                        const fromStr = r.amount_from ? `${r.amount_from.amount ?? ""} ${r.amount_from.currency ?? ""}`.trim() : "-";
                        const toStr = r.amount_to ? `${r.amount_to.amount ?? ""} ${r.amount_to.currency ?? ""}`.trim() : "-";
                        const chainName = r.payout?.name || r.payout?.id || "-";
                        const txHash = r.payout?.transaction_hash;
                        return (
                          <tr key={`paybis-${r.id}`} className="hover:bg-bl-accent/10/50 transition-colors">
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-100 font-mono">
                              {r.request_id ? `${r.request_id.slice(0, 8)}...` : "-"}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                              {r.created_at ? timeAgo(r.created_at) : "-"}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <TypePill type={r._flow === "sellCrypto" ? "Off-Ramp (Paybis)" : "On-Ramp (Paybis)"} />
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <SourcePill label="Paybis" />
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300 font-mono">
                              {r.partner_user_id ? `${r.partner_user_id.slice(0, 6)}...${r.partner_user_id.slice(-4)}` : "-"}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-300 align-top">
                              <FromToCell from={fromStr === "-" ? "" : fromStr} to={toStr === "-" ? "" : toStr} />
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-100 font-medium">
                              {fromStr !== "-" ? fromStr : toStr}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                              {r.amount_to?.currency || r.amount_from?.currency || "-"}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {chainName !== "-" ? <ChainPill label={chainName} /> : <span className="text-sm text-gray-400">-</span>}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <StatusBadge status={r.transaction_status} />
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">—</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300 font-mono">
                              <TxHash hash={txHash} network={chainName} url={r.payout?.explorer_link} />
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300 font-mono">-</td>
                          </tr>
                        );
                      }
                      const typeLabel = (r.tx_type || "").replace("_", "-");
                      const userAddr =
                        r.sending_address || r.recipient_address || "-";
                      const fromVal = r.initial_token || r.sending_address || "";
                      const toVal = r.recipient_token || r.recipient_address || "";
                      const token = r.initial_token || r.recipient_token || "-";
                      const chains = [r.network, r.recipient_network].filter(
                        Boolean
                      );
                      return (
                        <tr key={r.tx_hash || r.id} className="hover:bg-bl-accent/10/50 transition-colors">
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-100 font-mono">
                            <TxHash hash={r.tx_hash} network={r.network} url={r.explorer_urls?.primary} />
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                            {timeAgo(r.timestamp)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <TypePill type={typeLabel} />
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <SourcePill label={r.swap_partner} hash={r.tx_hash} network={r.network} />
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300 font-mono">
                            {userAddr !== "-" ? `${userAddr.slice(0, 6)}...${userAddr.slice(-4)}` : "-"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-300 align-top">
                            <FromToCell from={fromVal} to={toVal} />
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-100 font-medium">
                            {r.amount || "-"}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                            {token}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {chains.length ? (
                              <div className="flex flex-wrap gap-1">
                                {chains.map((c, i) => (
                                  <ChainPill key={i} label={c} />
                                ))}
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <StatusCell
                              id={r.id}
                              status={r.status}
                              canEdit={isSuperAdmin}
                              onSaved={() => txReq.refetch()}
                            />
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            {(() => {
                              if (r.tx_type !== "SWAP") return <span className="text-gray-500">—</span>;
                              const fs = r.fee_status;
                              if (fs === "captured" && r.our_fee_usd != null) {
                                return (
                                  <span className="text-green-400 font-medium font-mono">
                                    ${Number(r.our_fee_usd).toFixed(2)}
                                    {r.our_fee_pct != null && (
                                      <span className="text-gray-500 ml-1">({Number(r.our_fee_pct).toFixed(2)}%)</span>
                                    )}
                                  </span>
                                );
                              }
                              if (fs === "missing") {
                                return <span className="text-amber-400" title="Swap completed but no fee captured">— missing</span>;
                              }
                              return <span className="text-gray-500" title="No quote data forwarded">—</span>;
                            })()}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300 font-mono">
                            <TxHash hash={r.tx_hash} network={r.network} url={r.explorer_urls?.primary} />
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300 font-mono">
                            <TxHash hash={r.recipient_chain_tx_hash} network={r.recipient_network} url={r.explorer_urls?.secondary} />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6 pt-4 border-t border-gray-700">
              <button
                disabled={page === 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-4 py-2 text-sm font-normal text-gray-300 bg-gray-800 border border-gray-700 rounded-md hover:bg-bl-accent/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← Previous
              </button>
              <div className="text-sm text-gray-400">
                Page {page} of {Math.max(1, Math.ceil(total / pageSize))} • {total} total
              </div>
              <button
                disabled={loading || page >= Math.max(1, Math.ceil(total / pageSize))}
                onClick={() => setPage((p) => p + 1)}
                className="px-4 py-2 text-sm font-normal text-gray-300 bg-gray-800 border border-gray-700 rounded-md hover:bg-bl-accent/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Transactions;
